import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { RootState } from '../../../reducers';
import { ActivatedRoute } from '@angular/router';
import { navigate, setBrowserTitle, setTitle } from '../../../actions/core.actions';
import { Log } from '../../../utils/log';
import { findPlan, findPlans, initializeFindPlansState, initializeFindPlanState } from '../../../actions/plan.actions';
import { getPlan, getPlans } from '../../../selectors/plan.selectors';
import { filter, shareReplay, take } from 'rxjs/operators';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { Plan, PlanAssignment } from '../../../models/plan';
import { CurrentDateTime } from '../../../models/current-date-time';
import { getCurrentDateTime as getCurrentDateTimeSelector } from '../../../selectors/current-date-time.selectors';
import { User } from '../../../models/user';
import { getUsers } from '../../../selectors/user.selectors';
import { HighSchool, Subject } from '../../../models/common-data';
import { getHighSchool, getSubject } from '../../../selectors/static-data.selectors';
import { getCurrentDateTime, initializeGetCurrentDateTime } from '../../../actions/current-date-time.actions';
import { UserSearchCondition } from '../../../models/user-search-condition';
import { findUsers, initializeFindUsers } from '../../../actions/user.actions';
import { PlanUtil, PriorityRecord } from '../../../utils/plan-util';
import {
  PLAN_ASSIGN_MEMBER_DIALOG_HEIGHT,
  PLAN_ASSIGN_MEMBER_DIALOG_WIDTH,
  PLAN_UNASSIGN_MEMBER_DIALOG_HEIGHT,
  PLAN_UNASSIGN_MEMBER_DIALOG_WIDTH,
  DIALOG_ZERO_PADDING_PANEL_CLASS,
  PLAN_NAMES,
  PlanStatuses,
  PlanSubjectId,
  MEMBER_CONTACT_URL,
  SCHOOL_ORGANIZATION_ID,
  JUKU_ORGANIZATION_ID,
  DAIGAKU_ORGANIZATION_ID
} from '../../../resources/config';
import { SubjectUtil } from '../../../utils/subject-util';
import { getSignedInUser } from '../../../selectors/auth.selectors';
import { StaticDataUtil } from '../../../utils/static-data-util';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { enter } from '../../../resources/animations';
import { RoutingPathResolver } from '../../../app-routing-path-resolver';
import { UserUtil } from 'src/app/utils/user-util';
import {
  PlanAssignMemberDialogComponent,
  PlanAssignMemberDialogData
} from '../plan-assign-member-dialog/plan-assign-member-dialog.component';
import {
  PlanUnassignMemberDialogComponent,
  PlanUnassignMemberDialogData
} from '../plan-unassign-member-dialog/plan-unassign-member-dialog.component';
import { Juku } from 'src/app/models/juku';
import { getJukus } from 'src/app/selectors/juku.selectors';
import { JukuUtil } from 'src/app/utils/juku-util';
import { findJukus, initializeFindJukusState } from 'src/app/actions/juku.actions';
import { getDaigakus } from 'src/app/selectors/daigaku.selectors';
import { Daigaku } from 'src/app/models/daigaku';
import { DaigakuUtil } from 'src/app/utils/daigaku-util';
import { findDaigakus, initializeFindDaigakusState } from 'src/app/actions/daigaku.actions';
import { Breadcrumb } from 'src/app/models/menu';

interface AssignmentMember {
  id: string;
  name: string;
}

interface AssignmentData {
  subjectId: string;
  subjectName: string;
  members: AssignmentMember[];
}

@Component({
  selector: 'app-plan-assign',
  templateUrl: './plan-assign.component.html',
  styleUrls: ['./plan-assign.component.scss'],
  animations: [enter]
})
export class PlanAssignComponent implements OnInit, OnDestroy {
  isAdmin = false;
  isOrganizationAdmin = false;
  schoolName = '';
  plan$!: Observable<Plan>;
  plan!: Plan;
  plans$!: Observable<Plan[]>;
  plans: Plan[] = [];
  currentDatetime!: CurrentDateTime;
  assignments: AssignmentData[] = [];
  priorityRecord!: PriorityRecord;
  memberNum = 0;
  memberCount = 0;
  maxAssignableNum = 0;
  planSubscription!: Subscription;
  planName = '';
  isAnyAllPlan = false; // 全科目プランまたは全科目＋推薦・総合プランを判断するフラグ
  isAllPlan = false; // 全科目プランを判断するフラグ
  subjects$!: Observable<Subject[]>;
  planActiveStatus: string = PlanStatuses.ACTIVE;
  planEndedStatus: string = PlanStatuses.ENDED;
  planReserveStatus: string = PlanStatuses.RESERVE;
  isDataLoading$: Observable<boolean> = of(false);
  users: User[] = [];
  isNoAssignableMember = false;
  isMaxMember = false;
  isEndPlan = false;
  memberContactUrl: string = MEMBER_CONTACT_URL;
  breadcrumbs: Breadcrumb[] = [];

  displayedColumns: string[] = ['planStatus', 'validityPeriod', 'memberCount', 'memberList', 'action'];

  private LOG_SOURCE = this.constructor.name;
  private title = 'プランメンバー割当';

  constructor(private store: Store<RootState>, private activatedRoute: ActivatedRoute, private dialog: MatDialog) {}

  ngOnInit(): void {
    this.store.dispatch(setBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));

    this.setUp();
  }

  ngOnDestroy(): void {
    this.store.dispatch(initializeFindUsers());
    this.planSubscription.unsubscribe();
    this.store.dispatch(initializeFindPlanState());
    this.store.dispatch(initializeFindPlansState());
    this.store.dispatch(initializeGetCurrentDateTime());
    this.store.dispatch(initializeFindJukusState());
    this.store.dispatch(initializeFindDaigakusState());
  }

  breadcrumbsClickHandler(key: string) {
    switch (key) {
      case 'admin':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveAdmin() }));
        break;
      case 'admin/schools':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveSchools() }));
        break;
      case 'admin/plans':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolvePlans(this.plan.organization, this.plan.schoolId) }));
        break;
      case 'admin/jukus':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveJukus() }));
        break;
      case 'admin/daigakus':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveDaigakus() }));
        break;
    }
  }

  private setUp() {
    const planId = this.activatedRoute.snapshot.paramMap.get('planId');
    Log.debug(this.LOG_SOURCE, `query params: `, planId);
    this.isDataLoading$ = of(true);

    if (!planId) {
      Log.debug(this.LOG_SOURCE, `プランID:${planId}が存在しません。`);
      return null;
    }

    this.plan$ = this.store.select(getPlan).pipe(
      filter<Plan>(it => it != null),
      shareReplay(1)
    );

    this.store.dispatch(findPlan({ planId }));

    const signedInUser$: Observable<User> = this.store.select(getSignedInUser).pipe(
      filter<User>(it => it != null && it !== 'none'),
      shareReplay(1)
    );

    this.planSubscription = combineLatest([this.plan$, signedInUser$]).subscribe(([plan, signedInUser]) => {
      if (!plan.id) {
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveSearch() }));
        return;
      }

      this.isAdmin = signedInUser.isAdmin;
      this.isOrganizationAdmin = signedInUser.isOrganizationAdmin;

      if (!this.isAdmin) this.displayedColumns = this.displayedColumns.filter(column => column !== 'action');

      if (!this.isAdmin && (plan.schoolId !== signedInUser.schoolId || plan.organization !== signedInUser.organization)) {
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveSearch() }));
        return;
      }
      if (plan.subjectId === PlanSubjectId.TRIAL && this.isOrganizationAdmin) {
        this.goOrganizationAdminPlans();
      }
      this.plan = plan;
      this.planName = PLAN_NAMES[plan.subjectId as keyof typeof PLAN_NAMES];
      this.isAnyAllPlan = plan.subjectId === PlanSubjectId.ALL || plan.subjectId === PlanSubjectId.ALL_WITH_SPECIAL_ADMISSIONS;
      this.isAllPlan = plan.subjectId === PlanSubjectId.ALL;

      this.setUpData();
    });
  }

  private setUpData() {
    this.store.dispatch(findPlans({ organization: this.plan.organization, schoolId: this.plan.schoolId }));

    const currentDateTime$: Observable<CurrentDateTime> = this.store.select(getCurrentDateTimeSelector).pipe(
      filter(it => it != null),
      shareReplay(1)
    );
    const users$: Observable<User[]> = this.store.select(getUsers).pipe(
      filter(it => it != null),
      shareReplay(1)
    );
    const plans$: Observable<Plan[]> = this.store.select(getPlans).pipe(
      filter(it => it != null),
      shareReplay(1)
    );
    const highSchools$: Observable<HighSchool[]> = this.store.select(getHighSchool).pipe(
      filter(it => it != null),
      shareReplay(1)
    );
    const jukus$: Observable<Juku[]> = this.store.select(getJukus).pipe(
      filter(it => it != null),
      shareReplay(1)
    );
    const daigakus$: Observable<Daigaku[]> = this.store.select(getDaigakus).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    const subjects$ = this.store.select(getSubject).pipe(
      filter(it => it != null),
      shareReplay(1)
    );

    combineLatest([currentDateTime$, users$, plans$, highSchools$, jukus$, daigakus$, subjects$])
      .pipe(take(1))
      .subscribe(([currentDateTime, users, plans, highSchools, jukus, daigakus, subjects]) => {
        this.users = users;
        this.plans = plans;

        if (this.plan.organization === SCHOOL_ORGANIZATION_ID) {
          this.schoolName = StaticDataUtil.getHiSchoolName(highSchools, this.plan.schoolId);
        } else if (this.plan.organization === JUKU_ORGANIZATION_ID) {
          const juku: Juku = JukuUtil.getJukuByJukuCode(jukus, this.plan.schoolId);
          if (juku !== undefined && juku !== null) {
            this.schoolName = JukuUtil.getDisplayJukuName(juku);
          }
        } else if (this.plan.organization === DAIGAKU_ORGANIZATION_ID) {
          const daigaku: Daigaku = DaigakuUtil.getDaigakuByDaigakuCode(daigakus, this.plan.schoolId);
          if (daigaku !== undefined && daigaku !== null) {
            this.schoolName = DaigakuUtil.getDisplayDaigakuName(daigaku);
          }
        }

        if (this.schoolName === '') {
          this.store.dispatch(navigate({ url: RoutingPathResolver.resolveAdmin() }));
        }

        this.breadcrumbs = [];
        this.breadcrumbs.push({ key: 'admin', label: 'Admin機能' });

        if (this.plan.organization === SCHOOL_ORGANIZATION_ID) {
          this.breadcrumbs.push({ key: 'admin/schools', label: 'アカウント管理 - 学校' });
        } else if (this.plan.organization === JUKU_ORGANIZATION_ID) {
          this.breadcrumbs.push({ key: 'admin/jukus', label: 'アカウント管理 - 塾・予備校' });
        } else if (this.plan.organization === DAIGAKU_ORGANIZATION_ID) {
          this.breadcrumbs.push({ key: 'admin/daigakus', label: 'アカウント管理 - 大学' });
        }

        this.breadcrumbs.push({ key: 'admin/plans', label: this.schoolName + ' - プラン一覧' });
        this.breadcrumbs.push({ key: '', label: this.planName });

        this.currentDatetime = currentDateTime;
        this.priorityRecord = PlanUtil.getPriorityRecord(this.plan.records, currentDateTime);

        const excludedSubjectIds = [PlanSubjectId.ALL, PlanSubjectId.ALL_WITH_SPECIAL_ADMISSIONS];
        const planSubjectIds = Object.keys(PlanSubjectId)
          .map(key => PlanSubjectId[key as keyof typeof PlanSubjectId])
          .filter(value => !excludedSubjectIds.includes(value));
        this.assignments = planSubjectIds
          .map(planSubjectId => {
            // 既存の割り当てを取得
            const existingAssignment: PlanAssignment | undefined = this.priorityRecord.record.assignment.find(
              assignment => assignment.subjectId === planSubjectId
            );

            // 既存の割り当てがある場合は、その割り当てをセットする
            if (existingAssignment) {
              return this.setUpAssignment(existingAssignment, users, subjects);
            }

            // 既存の割り当てがない場合、新規で割り当てを作成する必要がある科目かを判定
            if (!this.shouldCreateAssignment(planSubjectId)) {
              return null;
            }
            // 新規の割り当てオブジェクトを作成
            const newAssignment: PlanAssignment = { subjectId: planSubjectId, memberIds: [] };
            return this.setUpAssignment(newAssignment, users, subjects);
          })
          .filter(it => it !== null);
        this.memberNum = PlanUtil.getAssignmentMemberCount(this.priorityRecord.record);
        this.memberCount = this.priorityRecord.record.memberCount;

        //// メッセージフラグ
        if (this.memberCount - this.memberNum < 1) {
          // メンバーが上限に達しこれ以上登録できない。上限に達し「+」が表示されない場合
          this.isMaxMember = true;
        } else {
          this.isMaxMember = false;
          // 割当枠に空きがあるが、メンバーがいない場合
          // アサイン可能チェック
          const assignableUsers = users.filter(user => PlanUtil.isAssignableUsers(this.plan, user, plans, currentDateTime));
          this.isNoAssignableMember = assignableUsers.length < 1;
          // 登録できるメンバーの上限数
          this.maxAssignableNum = this.memberCount - this.memberNum;
        }

        this.isEndPlan = this.priorityRecord.status === PlanStatuses.ENDED;

        this.isDataLoading$ = of(false);
      });

    this.store.dispatch(getCurrentDateTime());
    const userSearchCondition: UserSearchCondition = {
      organization: this.plan.organization,
      schoolId: this.plan.schoolId
    };
    this.store.dispatch(findUsers({ userSearchCondition }));
    this.store.dispatch(findJukus({}));
    this.store.dispatch(findDaigakus({}));
  }

  private shouldCreateAssignment(planSubjectId: string): boolean {
    // 各科目プラン・体験プランの場合の割り当て用のオブジェクトの作成は不要
    if (!this.isAnyAllPlan) return false;

    // 全科目プランの割り当て時、体験プランへの割り当てはしないのでオブジェクトの作成はしない
    if (planSubjectId === PlanSubjectId.TRIAL) return false;

    // 全科目プランの場合は全科目＋推薦・総合プランの割り当ては作成しない
    if (planSubjectId === PlanSubjectId.SPECIAL_ADMISSIONS && this.isAllPlan) return false;

    return true;
  }

  private setUpAssignment(assignment: PlanAssignment, users: User[], subjects: Subject[]): AssignmentData {
    const members: AssignmentMember[] = [];

    for (const memberId of assignment.memberIds) {
      members.push({ id: memberId, name: UserUtil.getUserName(users, memberId) });
    }

    return {
      subjectId: assignment.subjectId,
      subjectName: this.isAnyAllPlan ? SubjectUtil.getName(subjects, assignment.subjectId) : '',
      members
    };
  }

  menuClickHandler(key: string) {
    switch (key) {
      case 'fact_check_black':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolvePlanStatuses(this.plan.id) }));
        break;
    }
  }

  goMembers() {
    if (this.isAdmin) {
      this.store.dispatch(navigate({ url: RoutingPathResolver.resolveMembers(this.plan.organization, this.plan.schoolId) }));
    } else {
      this.store.dispatch(navigate({ url: RoutingPathResolver.resolveMembers() }));
    }
  }

  goOrganizationAdminPlans() {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolvePlans() }));
  }

  goHelp() {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveHelp() }));
  }

  assignMember(subjectId: string) {
    const config: MatDialogConfig<PlanAssignMemberDialogData> = {
      height: PLAN_ASSIGN_MEMBER_DIALOG_HEIGHT,
      width: PLAN_ASSIGN_MEMBER_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      data: {
        isAdmin: this.isAdmin,
        plan: this.plan,
        subjectId,
        plans: this.plans,
        currentDatetime: this.currentDatetime,
        maxAssignableNum: this.maxAssignableNum
      },
      disableClose: true
    };
    this.dialog.open(PlanAssignMemberDialogComponent, config);
    this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
      this.store.dispatch(initializeFindUsers());
      this.store.dispatch(initializeFindPlansState());
      this.store.dispatch(initializeGetCurrentDateTime());
      this.store.dispatch(findPlan({ planId: this.plan.id }));
    });
  }

  unAssignMember(subjectId: string, memberId: string) {
    const user = this.users.find(user => user.id === memberId);
    if (!user) {
      Log.debug(this.LOG_SOURCE, `ユーザーID:${memberId}のユーザーが見つからないためダイアログを表示できません。`);
      return;
    }

    const config: MatDialogConfig<PlanUnassignMemberDialogData> = {
      height: PLAN_UNASSIGN_MEMBER_DIALOG_HEIGHT,
      width: PLAN_UNASSIGN_MEMBER_DIALOG_WIDTH,
      panelClass: DIALOG_ZERO_PADDING_PANEL_CLASS,
      data: {
        planId: this.plan.id,
        subjectId,
        user
      },
      disableClose: true
    };
    this.dialog.open(PlanUnassignMemberDialogComponent, config);
    this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
      this.store.dispatch(initializeFindUsers());
      this.store.dispatch(initializeFindPlansState());
      this.store.dispatch(initializeGetCurrentDateTime());
      this.store.dispatch(findPlan({ planId: this.plan.id }));
    });
  }
}
